import { money, toShortName, firstNonBlank } from '@shared/lib';
import { t } from '@shared/i18n';
import type { GroupDto, GroupIndexItem } from './schemas';
import type { Group, GroupId, GroupType } from '../model/types';

const GROUP_TYPES: readonly GroupType[] = [
  'trip',
  'home',
  'friends',
  'office',
  'roommates',
  'others',
];

function toGroupType(value: string): GroupType {
  return (GROUP_TYPES as readonly string[]).includes(value) ? (value as GroupType) : 'others';
}

/** Anti-corruption: snake_case DTO → clean camelCase domain with rich types. */
export function toGroup(dto: GroupDto): Group {
  return {
    id: dto.id as GroupId,
    name: dto.name,
    type: 'others',
    memberCount: dto.member_count,
    currency: dto.currency,
    currencySymbol: '',
    preferredCurrencyId: '',
    yourBalance: money(dto.your_balance_minor),
    isArchived: false,
    isClosed: false,
    sharedAmount: money(0),
    privateAmount: money(0),
    walletBalance: null,
    walletId: null,
    walletBookkeeperIds: [],
    settlements: [],
    walletSettlements: [],
    members: [],
    // This DTO carries no transaction-timestamp field to derive it from; default to the
    // status-quo (member-count) display rather than presuming an empty group.
    hasExpenses: true,
    createdById: null,
    createdAt: dto.created_at
      ? new Date(dto.created_at)
      : dto.updated_at
      ? new Date(dto.updated_at)
      : null,
    closedAt: dto.closed_at ? new Date(dto.closed_at) : null,
    lastTransactionAt: null,
    updatedAt: new Date(dto.updated_at),
  };
}

/** Build the USER↔USER settlements with counterparty names resolved from the member list. */
function toSettlements(dto: GroupIndexItem): Group['settlements'] {
  const nameById = new Map<string, string>();
  for (const u of dto.users) {
    // An invited-by-phone member has no name/full_name yet — fall back to their phone number
    // rather than leaving the settlement line blank (e.g. "Receive from  ₹843.75"). `firstNonBlank`
    // (not `??`/`||`) because some rows send '' or a whitespace-only name, not null/undefined, for
    // "no name yet" — confirmed live (a Paid-by row rendered a blank label with the phone floating
    // separately, since the whitespace name was truthy enough to win the naive fallback). Shorten a
    // real name to "First L" (mobile's convention) — a phone number has no space, so it passes
    // through toShortName unchanged.
    if (u.user_id) {
      // The account's OWN `full_name` outranks the member doc's `name` (the inviter's phonebook
      // label, frozen at invite time and never synced on merge) — matching the backend's
      // transaction-feed resolution, so a merged invitee shows THEIR registered name here too.
      const rawName = u.is_guest
        ? firstNonBlank(u.full_name, u.name) || t.common.guest
        : firstNonBlank(u.full_name, u.name, u.phone_number);
      nameById.set(u.user_id, toShortName(rawName));
    }
  }
  return (dto.settle_data ?? [])
    .filter((s) => s.from.type.toLowerCase() === 'user' && s.to.type.toLowerCase() === 'user')
    .map((s) => ({
      fromId: s.from.id,
      fromName: nameById.get(s.from.id) ?? '',
      toId: s.to.id,
      toName: nameById.get(s.to.id) ?? '',
      amount: money(Math.round(s.amount)),
    }));
}

/**
 * The wallet's ACCOUNT id (the `paid_by` id for a wallet payer). Prefer the wallet object's own id;
 * fall back to the wallet side of any wallet-involving settlement. Null when there's no wallet.
 */
function toWalletId(dto: GroupIndexItem): string | null {
  const own = dto.wallet?.wallet_id ?? dto.wallet?._id ?? dto.wallet?.id;
  if (own) return own;
  for (const s of dto.settle_data ?? []) {
    if (s.from.type.toLowerCase() === 'wallet') return s.from.id;
    if (s.to.type.toLowerCase() === 'wallet') return s.to.id;
  }
  return null;
}

/** Wallet-involving settlements (either side is a `wallet`) — used for the user's wallet share. */
function toWalletSettlements(dto: GroupIndexItem): Group['walletSettlements'] {
  return (dto.settle_data ?? [])
    .filter((s) => s.from.type.toLowerCase() === 'wallet' || s.to.type.toLowerCase() === 'wallet')
    .map((s) => ({ fromId: s.from.id, toId: s.to.id, amount: money(Math.round(s.amount)) }));
}

/** Map a group from the real `GET /api/v1/group` list (different wire shape than detail). */
export function toGroupListItem(dto: GroupIndexItem): Group {
  const timestamp = dto.last_transaction_at ?? dto.created_at;
  return {
    id: dto._id as GroupId,
    name: dto.name,
    type: toGroupType(dto.type),
    memberCount: dto.users.length,
    currency: dto.currency?.currency_code ?? 'USD',
    currencySymbol: dto.currency?.currency_symbol ?? '',
    preferredCurrencyId: dto.user_preferred_currency_id ?? '',
    // Backend balance is already in MINOR units (the money model is integer minor units) —
    // brand it directly so `fromMinorUnit` formats it correctly (do NOT scale by 100).
    yourBalance: money(Math.round(dto.current_statement_balance)),
    isArchived: dto.is_archived,
    isClosed: dto.is_closed,
    sharedAmount: money(Math.round(dto.total_shared_amount)),
    privateAmount: money(Math.round(dto.total_private_amount)),
    // Gate on `virtual_wallet` too, not just whether the `wallet` object is present — the backend
    // can still send wallet stats for a group whose wallet feature is toggled off, which was
    // showing "Paid from: Wallet" as a payer option/default even then.
    walletBalance:
      dto.wallet && dto.virtual_wallet ? money(Math.round(dto.wallet.current_balance)) : null,
    isWalletClosed: Boolean(dto.wallet?.is_wallet_close),
    walletId: dto.virtual_wallet ? toWalletId(dto) : null,
    walletBookkeeperIds: dto.virtual_wallet
      ? [...new Set([...(dto.bookkeeper_ids ?? []), ...(dto.wallet?.bookkeeper_user_ids ?? [])])]
      : [],
    settlements: toSettlements(dto),
    walletSettlements: toWalletSettlements(dto),
    members: dto.users.map((u) => ({
      id: u.user_id ?? '',
      // The account's OWN `full_name` first: an invitee who registered via the invite link keeps
      // the inviter's phonebook label in the member doc's `name` forever (the merge never syncs
      // it), while `full_name` is their real registered name — same priority as the backend's
      // transaction feed. Unregistered invites have no `full_name`, so the contact label still
      // shows for them. `firstNonBlank`, not `??`: blank-but-not-null values (seen live on the
      // wire) must not shadow a perfectly good fallback.
      name: u.is_guest
        ? firstNonBlank(u.full_name, u.name) || t.common.guest
        : firstNonBlank(u.full_name, u.name),
      avatarUrl: u.profile_picture_url ?? null,
      phone: u.phone_number ?? '',
      email: u.email ?? '',
      role: u.role ?? null,
      isGuest: Boolean(u.is_guest),
      isPro: Boolean(u.is_pro),
    })),
    hasExpenses: dto.last_transaction_at != null,
    // `owner_id` first — the field mobile reads for its "(Creator)" tag — then the defensive
    // aliases, then a member row flagged `is_creator`. Feeds sending none leave it null (the
    // Creator tag stays hidden rather than guessing from roles — the backend reports role
    // "Owner" for every full member, so roles can't identify the creator).
    createdById:
      dto.owner_id ??
      dto.created_by_user_id ??
      dto.created_by ??
      dto.user_id ??
      dto.users.find((u) => u.is_creator)?.user_id ??
      null,
    startDate: dto.start_date ? new Date(dto.start_date) : null,
    endDate: dto.end_date ? new Date(dto.end_date) : null,
    createdAt: dto.created_at ? new Date(dto.created_at) : null,
    closedAt: dto.closed_at ? new Date(dto.closed_at) : null,
    lastTransactionAt: dto.last_transaction_at ? new Date(dto.last_transaction_at) : null,
    updatedAt: timestamp ? new Date(timestamp) : new Date(0),
  };
}
